// src/pages/UserReportPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom'; // Import Link for task links
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path if needed
import { useAuth } from '../context/AuthContext'; // Adjust path if needed
// --- FIX: Import utility functions ---
import { formatDurationBetweenDates, formatDate } from '../utils/styleUtils'; // Adjust path if needed
import styles from '../styles/UserReportPage.module.css'; // Adjust path if needed

const UserReportPage = () => {
    const { currentUser, userRole } = useAuth();

    // --- State ---
    const [employees, setEmployees] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedUserName, setSelectedUserName] = useState('');
    const [startDate, setStartDate] = useState(() => {
        const date = new Date(); date.setDate(date.getDate() - 30); return date.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [reportData, setReportData] = useState(null);
    const [taskList, setTaskList] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(true);
    const [loadingReport, setLoadingReport] = useState(false);
    const [error, setError] = useState('');

    // --- Fetch Employees ---
    useEffect(() => {
        const fetchEmployees = async () => {
            setLoadingEmployees(true); setError('');
            try {
                const usersCol = collection(db, 'users');
                const q = query(usersCol, orderBy('name', 'asc'));
                const snapshot = await getDocs(q);
                const userList = snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name || doc.data().email || 'Unnamed' }));
                setEmployees(userList);
            } catch (err) { console.error("Error fetching employees:", err); setError("Failed to load employee list.");
            } finally { setLoadingEmployees(false); }
        };
        if (userRole === 'Manager') { fetchEmployees(); }
        else { setLoadingEmployees(false); setError("Access Denied."); }
    }, [userRole]);

    // --- Report Generation Handler ---
    const handleGenerateReport = useCallback(async () => {
        if (!selectedUserId) { setError("Please select an employee."); return; }
        if (!startDate || !endDate) { setError("Please select a valid date range."); return; }
        if (new Date(startDate) > new Date(endDate)) { setError("Start date cannot be after end date."); return; }

        setLoadingReport(true); setError(''); setReportData(null); setTaskList([]);

        try {
            const startTimestamp = Timestamp.fromDate(new Date(startDate));
            const endOfDay = new Date(endDate); endOfDay.setHours(23, 59, 59, 999);
            const endTimestamp = Timestamp.fromDate(endOfDay);

            const tasksRef = collection(db, 'tasks');
            const q = query(tasksRef,
                where('assignedTo', 'array-contains', selectedUserId),
                where('createdAt', '>=', startTimestamp),
                where('createdAt', '<=', endTimestamp),
                orderBy('createdAt', 'desc')
            ); // Requires index: assignedTo (ArrayContains), createdAt (Descending)

            const taskSnapshot = await getDocs(q);
            const tasks = taskSnapshot.docs.map(doc => ({
                 id: doc.id, ...doc.data(),
                 createdAt: doc.data().createdAt?.toDate(),
                 acknowledgedAt: doc.data().acknowledgedBy?.[selectedUserId]?.toDate()
            }));

            // Calculate Metrics
            let completedCount = 0, cancelledCount = 0, acknowledgedCount = 0;
            let totalResponseMillis = 0;
            tasks.forEach(task => {
                if (task.status === 'Completed') completedCount++;
                if (task.status === 'Cancelled') cancelledCount++;
                if (task.acknowledgedAt && task.createdAt) {
                    acknowledgedCount++;
                    totalResponseMillis += (task.acknowledgedAt.getTime() - task.createdAt.getTime());
                }
            });
            const averageResponseMillis = acknowledgedCount > 0 ? totalResponseMillis / acknowledgedCount : 0;
            const averageResponseTime = acknowledgedCount > 0 ? formatDurationBetweenDates(new Date(0), new Date(averageResponseMillis)) : 'N/A';

            setReportData({ totalAssigned: tasks.length, completedCount, cancelledCount, acknowledgedCount, averageResponseTime });
            setTaskList(tasks);

        } catch (err) {
            console.error("Error generating report:", err);
             if (err.code === 'failed-precondition') { setError("Database indexing required. Check Firestore indexes."); }
             else { setError("Failed to generate report."); }
             setReportData(null); setTaskList([]);
        } finally { setLoadingReport(false); }
    }, [selectedUserId, startDate, endDate]);

    // Handle employee selection
    const handleEmployeeSelect = (e) => {
        const userId = e.target.value;
        setSelectedUserId(userId);
        const employee = employees.find(emp => emp.id === userId);
        setSelectedUserName(employee ? employee.name : '');
        setReportData(null); setTaskList([]); // Clear previous report
    };

    // --- Render Logic ---
    return (
        <div className={styles.pageContainer}>
            <h2 className={styles.title}>User Performance Report</h2>

            {/* Selection Controls */}
            <div className={styles.controlsContainer}>
                <div className={styles.controlGroup}>
                    <label htmlFor="employeeSelect">Select Employee:</label>
                    <select id="employeeSelect" value={selectedUserId} onChange={handleEmployeeSelect} disabled={loadingEmployees || loadingReport} >
                        <option value="" disabled>-- Select --</option>
                        {loadingEmployees ? ( <option disabled>Loading...</option> ) : ( employees.map(emp => ( <option key={emp.id} value={emp.id}>{emp.name}</option> )) )}
                    </select>
                </div>
                <div className={styles.controlGroup}>
                    <label htmlFor="startDate">Start Date:</label>
                    <input type="date" id="startDate" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={loadingReport} />
                </div>
                 <div className={styles.controlGroup}>
                    <label htmlFor="endDate">End Date:</label>
                    <input type="date" id="endDate" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={loadingReport} min={startDate || ''} />
                </div>
                 <button onClick={handleGenerateReport} className={styles.generateButton} disabled={!selectedUserId || loadingReport || loadingEmployees} >
                    {loadingReport ? <span className={styles.loadingSpinnerSmall}></span> : 'Generate Report'}
                </button>
            </div>

            {/* Error Display */}
            {error && <p className={styles.errorMessage}>{error}</p>}

            {/* Report Display Area */}
            {loadingReport && <div className={styles.loadingIndicator}>Generating report...</div>}

            {reportData && !loadingReport && (
                <div className={styles.reportContainer}>
                    <h3 className={styles.reportTitle}>Performance Report for {selectedUserName}</h3>
                    <p className={styles.reportPeriod}>
                        {/* --- FIX: Use imported formatDate --- */}
                        Period: {formatDate(new Date(startDate))} to {formatDate(new Date(endDate))}
                    </p>
                    {/* Metrics Grid */}
                    <div className={styles.metricsGrid}>
                        <div className={styles.metricItem}> <span className={styles.metricValue}>{reportData.totalAssigned}</span> <span className={styles.metricLabel}>Tasks Assigned</span> </div>
                        <div className={styles.metricItem}> <span className={styles.metricValue}>{reportData.completedCount}</span> <span className={styles.metricLabel}>Tasks Completed</span> </div>
                        <div className={styles.metricItem}> <span className={styles.metricValue}>{reportData.cancelledCount}</span> <span className={styles.metricLabel}>Tasks Cancelled</span> </div>
                        <div className={styles.metricItem}> <span className={styles.metricValue}>{reportData.acknowledgedCount} / {reportData.totalAssigned}</span> <span className={styles.metricLabel}>Tasks Acknowledged</span> </div>
                        <div className={styles.metricItem}> <span className={styles.metricValue}>{reportData.averageResponseTime}</span> <span className={styles.metricLabel}>Avg. Ack. Time</span> </div>
                    </div>
                    {/* Task List */}
                    <h4 className={styles.taskListTitle}>Tasks Included in Report ({taskList.length})</h4>
                    <ul className={styles.taskList}>
                        {taskList.length === 0 ? ( <li>No tasks found for this period.</li> )
                        : (
                            taskList.map(task => (
                                <li key={task.id}>
                                    <Link to={`/task/${task.id}`} className={styles.taskLink}>{task.taskName}</Link>
                                    <span> - Status: {task.status}</span>
                                    <span> - Priority: {task.priority || 'Medium'}</span>
                                    {/* --- FIX: Use imported formatDate --- */}
                                    <span> - Created: {formatDate(task.createdAt)}</span>
                                    {task.acknowledgedAt && <span> - Ack: {formatDate(task.acknowledgedAt, {dateStyle: 'short', timeStyle: 'short'})}</span>}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default UserReportPage;
